import type { Article } from "../articles";
import { PRICING_2026 } from "../guides-data";

const p = PRICING_2026.flooring;

export const articleCompleteFlooringGuide: Article = {
  slug: "complete-flooring-guide",
  title: "Flooring Installation & Cost Guide: Hardwood, LVP, Tile, and Subfloors",
  description: "A comprehensive manual on selecting and installing hardwood, luxury vinyl plank, and tile floors. Learn professional tolerances, subfloor prep, underlayment specs, and actual installation costs.",
  category: "Flooring",
  tags: ["Flooring", "Hardwood", "LVP", "Tiling", "Cost Estimation"],
  createdAt: "July 14, 2026",
  updatedAt: "July 14, 2026",
  lastVerified: "July 14, 2026",
  readTime: "9 min read",
  keyTakeaway: "A successful flooring installation relies on a flat, dry subfloor and correct waste/expansion planning. Subfloors must meet a flatness tolerance of 1/8 inch over 10 feet. Solid hardwood requires perimeter expansion gaps and careful moisture acclimation per NWFA guidelines. Luxury vinyl plank (LVP) demands a 12-20 mil wear layer for heavy traffic, and tile projects need a 10-15% waste buffer.",
  author: { name: "Mauricio L.", url: "/authors/mauricio-l/" },
  sources: [
    {
      name: "National Wood Flooring Association (NWFA) - Technical Installation Guidelines",
      url: "https://www.nwfa.org"
    },
    {
      name: "Resilient Floor Covering Institute (RFCI) - Resilient Flooring Standards",
      url: "https://rfci.com"
    },
    {
      name: "Tile Council of North America (TCNA) - Installation Handbook",
      url: "https://www.tcnatile.com"
    }
  ],
  keyMetrics: [
    { label: "Subfloor Flatness Tolerance", value: "1/8\" over 10 ft" },
    { label: "LVP Wear Layer Range", value: "12 - 20 mil" },
    { label: "Tile Waste Buffer", value: "10% - 15%" },
    { label: "Hardwood Expansion Gap", value: "3/4\" perimeter" },
    { label: "LVP Installed Cost / SF", value: `$${p.costPerSqFtInstalled.vinylLVP.min.toFixed(2)} - $${p.costPerSqFtInstalled.vinylLVP.max.toFixed(2)}` },
    { label: "Hardwood Installed Cost / SF", value: `$${p.costPerSqFtInstalled.hardwood.min.toFixed(2)} - $${p.costPerSqFtInstalled.hardwood.max.toFixed(2)}` }
  ],
  faqs: [
    {
      question: "What subfloor flatness is required for flooring installation?",
      answer: "Per NWFA and TCNA industry standards, the subfloor flatness tolerance must be within 1/8 inch over a 10-foot radius (or 3/16 inch over 10 feet for some thinner floating floors). Gaps larger than this put pressure on locking joints or cause tile to crack."
    },
    {
      question: "How do I choose the correct LVP wear layer thickness?",
      answer: "The wear layer thickness is measured in mils (thousandths of an inch). A wear layer of 12-20 mil is recommended for high-traffic residential areas, whereas commercial areas typically require 20 mil or higher. This wear layer is separate from the total thickness of the plank, which is measured in millimeters (mm)."
    },
    {
      question: "How much extra material should I order for waste?",
      answer: "For standard hardwood or LVP installations, a 10% waste buffer is recommended. For tile installations, a 10% to 15% waste buffer is required to account for complex layout cuts, perimeter adjustments, and potential tile breakage. You can calculate your target quantity with our tile calculator."
    }
  ],
  content: `
    <p>
      Installing new flooring is one of the most high-impact home improvement projects you can undertake. However, a beautiful finish is only as good as the prep work beneath it. Failing to account for subfloor conditions, thermal expansion, or material-specific tolerances leads to squeaks, joint separation, buckled boards, or cracked tiles.
    </p>
    <p>
      Whether you are installing solid hardwood, luxury vinyl plank (LVP), or ceramic tile, the secret to longevity lies in adhering to strict industry standards and accurate material estimation.
    </p>

    <!--COMPONENT:info-note|label="The Foundation First"-->
    Never compromise on subfloor preparation. Over 90% of flooring failures—including squeaks, cracks, and separated locking mechanisms—are caused by subfloor deficiencies rather than product defects.
    <!--ENDOFCOMPONENT-->

    <h2>1. Subfloor Flatness Tolerances: The Core Requirement</h2>
    <p>
      Before laying down a single plank or tile, the subfloor must be checked for flatness. The standard industry tolerance is <strong>1/8 inch of deflection over a 10-foot radius</strong>.
    </p>
    <p>
      If your subfloor has ridges or dips beyond these tolerances:
    </p>
    <ul>
      <li><strong>Floating Floors (LVP & Laminate):</strong> High and low spots cause the planks to flex underfoot, putting severe tension on click-lock joints until they shear and separate.</li>
      <li><strong>Solid Hardwood:</strong> Uneven surfaces lead to hollow spots, squeaking, and difficulty nailing the boards flat.</li>
      <li><strong>Tile:</strong> Dips cause "lippage" (uneven tile edges) and create voids in the thinset mortar, leaving tiles prone to cracking under load. Use our <a href="/calculators/area/" class="text-[var(--accent)] hover:underline font-semibold font-mono">area calculator</a> to measure your room size and ensure you have correct square footage measurements.</li>
    </ul>
    <p>
      To fix concrete subfloors, grind down high spots and use a high-quality self-leveling underlayment compound to fill low areas. For wood subfloors, secure loose plywood sheets to joists with deck screws and sand down high joints.
    </p>

    <h2>2. Hardwood Installation & NWFA Guidelines</h2>
    <p>
      Solid hardwood flooring is a lifetime investment, costing <strong>$${p.costPerSqFtInstalled.hardwood.min.toFixed(2)}–$${p.costPerSqFtInstalled.hardwood.max.toFixed(2)} per square foot installed</strong>. To ensure it lasts, the National Wood Flooring Association (NWFA) outlines critical installation protocols:
    </p>
    
    <h3>Acclimation and Moisture Control</h3>
    <p>
      Hardwood is hygroscopic: it expands in humid weather and contracts in dry conditions. Wood planks must acclimate in the installation space with active HVAC systems running for at least 3 to 7 days. Installers must measure the moisture content (MC) of both the subfloor and the hardwood using a professional pin or pinless moisture meter.
    </p>
    <p>
      According to the NWFA:
    </p>
    <ul>
      <li>For solid strip flooring (less than 3 inches wide), the MC difference between the subfloor and the hardwood must not exceed <strong>4%</strong>.</li>
      <li>For wide-plank flooring (3 inches or wider), the MC difference must be <strong>2% or less</strong> before installation.</li>
    </ul>

    <h3>Seasonal Expansion Gaps</h3>
    <p>
      Hardwood needs room to breathe. Installers must maintain a perimeter <strong>expansion gap of 3/4 inch</strong> (equal to the thickness of the flooring) around all walls, columns, and vertical obstructions. This gap is hidden under the baseboard and base shoe trim. To calculate exact budget allowances for your project, refer to our <a href="/articles/flooring-installation-cost-per-square-foot/" class="text-[var(--accent)] hover:underline font-semibold">flooring installation cost guide</a>.
    </p>

    <!--COMPONENT:comparison-table|json={"columns":[{"key":"material","label":"Flooring Type"},{"key":"cost","label":"Installed Cost / SF"},{"key":"expansion","label":"Expansion Gap Required"},{"key":"flatness","label":"Flatness Tolerance"},{"key":"lifespan","label":"Service Life"}],"rows":[{"material":"Solid Hardwood","cost":"$9.00 - $18.00","expansion":"3/4 inch","flatness":"1/8 inch over 10 ft","lifespan":"50+ Years (Refinishable)"},{"material":"Luxury Vinyl (LVP)","cost":"$4.00 - $8.00","expansion":"1/4 - 3/8 inch","flatness":"3/16 inch over 10 ft","lifespan":"15-25 Years"},{"material":"Porcelain/Ceramic Tile","cost":"$9.00 - $20.00","expansion":"1/4 inch (perimeter joints)","flatness":"1/8 inch over 10 ft","lifespan":"50+ Years"}]}--><!--ENDOFCOMPONENT-->

    <h2>3. Luxury Vinyl Plank (LVP) Wear Layer Specifications</h2>
    <p>
      LVP is the default choice for budget-conscious and high-moisture areas, running about <strong>$${p.costPerSqFtInstalled.vinylLVP.min.toFixed(2)}–$${p.costPerSqFtInstalled.vinylLVP.max.toFixed(2)} per square foot installed</strong>. To compare LVP with laminate or wood, check our detailed <a href="/articles/vinyl-vs-laminate-vs-hardwood-flooring/" class="text-[var(--accent)] hover:underline font-semibold">flooring type comparison guide</a>.
    </p>
    <p>
      When buying LVP, focus on the <strong>wear layer</strong> thickness, measured in <strong>mils</strong> (one-thousandth of an inch, not to be confused with millimeters, which represent the overall board thickness):
    </p>
    <ul>
      <li><strong>6 to 10 mil:</strong> Low-traffic or temporary areas. Prone to scratching from pet claws or dragging furniture.</li>
      <li><strong>12 mil:</strong> Standard residential grade. Ideal for bedrooms and secondary living spaces.</li>
      <li><strong>12-20 mil:</strong> Standard range recommended for residential high-traffic zones.</li>
      <li><strong>20 mil+:</strong> Heavy-duty residential and commercial grade. Essential for active households with pets, children, and high-foot-traffic areas like entryways and kitchens.</li>
    </ul>
    <p>
      Floating LVP floors require a perimeter expansion gap of <strong>1/4 to 3/8 inch</strong> to prevent buckled seams under temperature changes.
    </p>

    <h2>4. Tile Installation and Waste Calculations</h2>
    <p>
      Ceramic and porcelain tiles offer unmatched moisture protection and durability, but they are unforgiving. Tile installation requires a <strong>10% to 15% waste calculation buffer</strong> when ordering material.
    </p>
    <img src="/images/assets/flooring-tile-spacers.webp" alt="Tiling Leveling System Spacers Installation Detail" class="w-full h-auto display-block my-6 rounded-lg border border-border-custom" />
    <p>
      Why is the waste buffer so high compared to other flooring?
    </p>
    <ul>
      <li><strong>Perimeter Cuts:</strong> Tiles at the borders must be cut to fit. In complex rooms, these off-cuts are often unusable.</li>
      <li><strong>Laying Pattern:</strong> Standard straight lay (grid) projects require a 10% buffer. Diagonal, herringbone, or chevron patterns require a 15% buffer due to the higher volume of triangular edge cuts.</li>
      <li><strong>Breakage:</strong> A small percentage of tiles will inevitably crack during shipping, handling, or when using a tile saw or snap cutter.</li>
    </ul>
    <p>
      You can determine your exact box counts and grout requirements using our interactive <a href="/calculators/tile/" class="text-[var(--accent)] hover:underline font-semibold font-mono">tile calculator</a>. Additionally, if your remodel involves multiple surfaces, such as upgrading a kitchen or bathroom vanity, check our <a href="/calculators/countertop/" class="text-[var(--accent)] hover:underline font-semibold font-mono">countertop calculator</a> for project planning.
    </p>

    <!--COMPONENT:warning-note|label="Tile Layout Mistake"-->
    Do not skip dry-laying your tile before applying thinset mortar. Dry-laying allows you to adjust the starting line so that you don't end up with narrow, ugly slivers of tile along the main visible walls.
    <!--ENDOFCOMPONENT-->

    <h2>5. Underlayments and Moisture Barriers</h2>
    <p>
      Selecting the correct underlayment is essential for protecting your investment:
    </p>
    <img src="/images/assets/flooring-underlayment-detail.webp" alt="Soundproof Flooring Underlayment Foam Roll Out Detail" class="w-full h-auto display-block my-6 rounded-lg border border-border-custom" />
    <ul>
      <li><strong>Concrete Subfloors (Moisture):</strong> Concrete slabs emit moisture vapor. Always install a 6-mil polyethylene vapor barrier sheet under floating LVP or laminate floors to prevent mold growth and joint warping.</li>
      <li><strong>Wood Subfloors (Quiet & Comfort):</strong> Use high-density felt, cork, or specialized rubber underlayments to reduce sound transfer and add slight cushion. Never use carpet padding under floating planks.</li>
      <li><strong>Attached Pads:</strong> Many modern LVP and laminate products have pre-attached foam or cork underlayment. Do not add a secondary underlayment unless specifically approved by the manufacturer; excess compressibility will flex and snap the locking joints over time.</li>
    </ul>

    <h2>6. Project Cost Checklist</h2>
    <p>
      To estimate the total material and labor allowance for your room size, use our free <a href="/calculators/flooring/" class="text-[var(--accent)] hover:underline font-semibold font-mono">flooring calculator</a>. Before signing a contractor contract, ensure the bid addresses these points:
    </p>
    <ul>
      <li>Does the bid include old material demolition and haul-away fees?</li>
      <li>Is subfloor flat-prep (grinding/leveling) included or priced per hour?</li>
      <li>Are transition strips, door jamb undercutting, and baseboard reinstallation included?</li>
    </ul>
  `
};
